import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ImageCard from './ImageCard';
import JsonLd from './JsonLd';
import { SITE_URL } from '../seoConfig';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Plain-text section name from the block's HTML title (first heading).
const sectionName = (html) => {
  if (typeof document === 'undefined' || !html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const h = tmp.querySelector('h1, h2, h3, h4');
  return ((h ? h.textContent : tmp.textContent) || '').trim();
};

/**
 * FeaturedBlocks
 * --------------
 * Renders all enabled "Featured Blocks" defined in admin.
 * Each block has a HTML title and up to 6 items shown in a 3-col grid.
 * Each item: HTML title + photo card (no like button).
 */
const FeaturedBlocks = () => {
  const [blocks, setBlocks] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/featured-blocks`);
        if (!cancelled) setBlocks(data.blocks || []);
      } catch (err) {
        console.error('Failed to load featured blocks:', err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded || blocks.length === 0) return null;

  // JSON-LD: one lightweight ItemList per curated Featured Block. These sections
  // are admin-curated and stable (unlike the dynamic "Latest Photos" block, which
  // gets no structured data). Lightweight ImageObject: name/url/contentUrl/author.
  const itemListsLd = blocks.map((block) => {
    const items = (block.items || []).filter((it) => it.photo);
    if (items.length === 0) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: sectionName(block.title_html),
      itemListElement: items.map((it, idx) => {
        const p = it.photo;
        const url = `${SITE_URL}/photos/${p.id}-${p.alias}`;
        return {
          '@type': 'ListItem',
          position: idx + 1,
          url,
          item: {
            '@type': 'ImageObject',
            '@id': url,
            url,
            name: p.seo_title || p.title,
            contentUrl: p.image_urls?.full,
            thumbnailUrl: p.image_urls?.thumbnail,
            ...(p.photographer?.user_display_name
              ? { author: { '@type': 'Person', name: p.photographer.user_display_name } }
              : {}),
          },
        };
      }),
    };
  }).filter(Boolean);

  return (
    <>
      {itemListsLd.length > 0 && <JsonLd data={itemListsLd} />}
      {blocks.map((block) => (
        <section
          key={block._id}
          className="max-w-full md:max-w-[70%] mx-auto px-4 py-10"
          data-testid={`featured-block-${block._id}`}
        >
          {block.title_html && (
            <div
              className="mb-6 featured-block-title"
              dangerouslySetInnerHTML={{ __html: block.title_html }}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 min-[1920px]:grid-cols-4 gap-6">
            {(block.items || []).map((item, idx) => (
              <div
                key={`${block._id}-${idx}`}
                className="flex flex-col"
                data-testid={`featured-item-${block._id}-${idx}`}
              >
                {item.title_html && (
                  <div
                    className="mb-3 featured-item-title"
                    dangerouslySetInnerHTML={{ __html: item.title_html }}
                  />
                )}

                {item.photo ? (
                  <ImageCard
                    image={item.photo}
                    showPhotographer={true}
                    showTags={false}
                    photographerClickable={false}
                    showLike={false}
                    cropToSquare={true}
                  />
                ) : (
                  <div
                    className="aspect-square bg-gray-100 border border-dashed border-gray-300 rounded flex items-center justify-center text-sm text-gray-500"
                    data-testid={`featured-item-missing-${block._id}-${idx}`}
                  >
                    Photo not found (ID: {item.photo_id ?? '—'})
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Optional CTA button */}
          {block.button_text && block.button_url && (
            <div className="mt-6 flex justify-center">
              <a
                href={block.button_url}
                className="inline-block px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-base rounded transition-colors"
                data-testid={`featured-block-cta-${block._id}`}
              >
                {block.button_text}
              </a>
            </div>
          )}
        </section>
      ))}
    </>
  );
};

export default FeaturedBlocks;
